import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Input } from '@playcastdotio/Shadcn';
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { PlaycastContext } from '../../components/PlaycastWrapper';
import { faCircleStop, faFloppyDisk, faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { VolumeManager } from '../../components/layout/VolumeManager';
import { Display } from '../../components/widgets/Display';
import clsx from 'clsx';
import { isElectron } from '../../api';
import { DisplayPicker } from '../../components/widgets/DisplayPicker';
import { InviteButton } from '../../components/widgets/SendInvitePanel';
import { PlayerInputOptions } from '../../components/widgets/PlayerInputOptions';
import React, { memo } from 'react';
import constants from '@playcastdotio/GlobalConfig';
import { sessionCoordinator } from '../../coordinators/init';
import { USE_SESSION_COORDINATOR } from '../../config/featureFlags';

const LiveActionButton = memo(
  ({
    isLive,
    buttonText,
    handleStopStream,
    handleStartLoginStream,
  }: {
    isLive: boolean;
    buttonText: string;
    handleStopStream: () => void;
    handleStartLoginStream: (event: React.MouseEvent<HTMLButtonElement>) => void;
  }) => {
    const navigate = useNavigate();

    return isLive ? (
      <div className='flex w-full flex-col space-y-2 md:space-y-0'>
        <Button className='strong-shadows m-0 flex w-full p-0 text-white md:hidden' onClick={() => navigate('/playcast')}>
          Invite Players!
        </Button>
        <Button
          className='strong-shadows m-0 w-full gap-x-2 bg-orange-600 p-0 text-white hover:bg-orange-900 focus:bg-orange-900 active:bg-orange-900'
          onClick={handleStopStream}
        >
          <FontAwesomeIcon icon={faCircleStop} />
          Stop Playcasting
        </Button>
      </div>
    ) : (
      <Button
        className='embossed-primary strong-shadows m-0 w-full bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 font-extrabold opacity-100'
        onClick={handleStartLoginStream}
        // disabled={disableStartButton}
      >
        {buttonText}
      </Button>
    );
  },
);

export const Studio = () => {
  const context = useContext(PlaycastContext);

  const [streamTitle, setStreamTitle] = useState<string>('');
  const streamTitleInputRef = useRef<HTMLInputElement>(null);
  const [isLive, setIsLive] = useState(false);
  const [buttonText, setButtonText] = useState('Start Playcasting');
  // const [disableStartButton, setDisableStartButton] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState<string | null>('');
  const [streamUrl, setStreamUrl] = useState<string>('');

  useEffect(() => {
    const streamUrl = `${context.stateStores.invitationStateV2.type === 'cafe' ? constants.PAVILION_PLAYER_BASE_URL : constants.PRIME_PLAYER_BASE_URL}j/${context.stateStores.invitationStateV2.id}`;
    setStreamUrl(streamUrl);
  }, [context.stateStores.invitationStateV2.id, context.stateStores.invitationStateV2.type]);

  // const [inputPaused, setInputPaused] = useState(false);

  // useEffect(() => {
  //   setInputPaused(context.stateStores.pauseInputState.paused);
  // }, [context.stateStores.pauseInputState.paused]);

  useEffect(() => {
    // if the stream entry box is not selected, update the session title
    if (document.activeElement !== streamTitleInputRef.current) {
      setStreamTitle(context.stateStores.invitationStateV2.description ?? '');
    }
  }, [context.stateStores.invitationStateV2.description]);

  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    // Clear any existing timeout when effect reruns
    // if (timeoutRef.current) {
    //   clearTimeout(timeoutRef.current);
    // }

    const state = context.stateStores.hostState.status;
    console.log('state', state);

    if (state === 'streaming') {
      setIsLive(true);
      setIsLogin(false);
      if (document.hasFocus()) {
        navigator.clipboard.writeText(streamUrl);
      }
      if (context.electronFunctions) {
        context.electronFunctions.copyToClipboard(streamUrl);
      }
    } else if (state === 'stopping') {
      setIsLive(false);
      setButtonText('Stopping...');
    } else if (state === 'starting') {
      setIsLogin(false);
      setButtonText('Starting...');
      // Set new timeout and store its ID
      // timeoutRef.current = setTimeout(() => {
      //   // Get fresh status value when timeout executes
      //   const currentStatus = context.stateStores.hostState.status;
      //   console.error('Stream did not start in 5 seconds, stopping stream', currentStatus);
      //   if (currentStatus === 'starting') {
      //     context.targetFunctions.stopStream();
      //   }
      // }, 5000);
    } else {
      setIsLive(false);
      setIsLogin(false);
      setButtonText('Start Playcasting');
    }

    // Cleanup function to clear timeout when component unmounts or effect reruns
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [context.electronFunctions, context.stateStores.hostState.status, streamUrl]);

  useEffect(() => {
    if (context.stateStores.loginProfileState.accessToken && !context.stateStores.loginProfileState.accessToken.startsWith('hpl;')) {
      // setDisableStartButton(false);
      setIsLogin(false);
      setButtonText('Start Playcasting');
    } else {
      // setDisableStartButton(true);
      setIsLogin(true);
      setButtonText('Start Playcasting');
    }
  }, [context.stateStores.loginProfileState]);

  const [showSave, setShowSave] = useState(false);
  const [savedTitle, setSavedTitle] = useState(context.stateStores.invitationStateV2.description);
  const autoStartTriggered = useRef(false);

  const handleStreamTitleChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setStreamTitle(e.target.value);
    context.targetFunctions.setStreamTitle(e.target.value);
  };

  useEffect(() => {
    // show save button if the title has changed
    setShowSave(savedTitle !== context.stateStores.invitationStateV2.description);
  }, [savedTitle, context.stateStores.invitationStateV2.description]);

  useEffect(() => {
    // show save button if the title has changed
    setShowSave(savedTitle !== context.stateStores.invitationStateV2.description);
  }, [savedTitle, context.stateStores.invitationStateV2.description]);

  // UseEffect to check we are logged in and have a token
  useEffect(() => {
    const handleAutoStart = async () => {
      if (context.stateStores.hostState.edition !== 'standard') {
        return;
      }
      const waitingForLogin = localStorage.getItem('waitingForLogin');
      const isAuthenticated =
        context.stateStores.loginProfileState.accessToken && !context.stateStores.loginProfileState.accessToken.startsWith('hpl;');

      if (waitingForLogin && isAuthenticated && !autoStartTriggered.current) {
        autoStartTriggered.current = true;

        // Get saved title
        const savedTitle = localStorage.getItem('savedStreamTitle');

        // Clean up localStorage
        localStorage.removeItem('waitingForLogin');
        localStorage.removeItem('savedStreamTitle');

        // Delay the auto-start to allow page setup
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Update title if needed
        if (savedTitle && streamTitle !== savedTitle) {
          setStreamTitle(savedTitle);
          context.targetFunctions.updateInvitationDescription(savedTitle);
          setSavedTitle(savedTitle);
        }

        // Start the stream
        if (USE_SESSION_COORDINATOR) {
          void sessionCoordinator.startStream().catch((err) => console.error('[Studio] startStream failed', err));
        } else {
          context.targetFunctions.startStream();
        }
      }
    };

    handleAutoStart();
  }, [context.stateStores.loginProfileState, streamTitle, context.targetFunctions]);

  const handleStartLoginStream = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (context.stateStores.hostState.edition !== 'standard') {
      return;
    }
    event.preventDefault();

    const isAuthenticated =
      context.stateStores.loginProfileState.accessToken && !context.stateStores.loginProfileState.accessToken.startsWith('hpl;');

    if (!isAuthenticated) {
      if (isElectron) {
        // Save current state before login
        localStorage.setItem('waitingForLogin', 'true');
        localStorage.setItem('savedStreamTitle', streamTitle);

        // Trigger login
        context.electronFunctions?.hostLogin();
      } else {
        console.error('ERROR: User should be logged in to start stream at this point on non-electron...');
      }
    } else {
      setStreamTitle(context.stateStores.invitationStateV2.description ?? '');
      context.targetFunctions.updateInvitationDescription(streamTitle);
      setSavedTitle(streamTitle);
      if (USE_SESSION_COORDINATOR) {
        void sessionCoordinator.startStream().catch((err) => console.error('[Studio] startStream failed', err));
      } else {
        context.targetFunctions.startStream();
      }
    }
  };

  const handleStopStream = () => {
    if (USE_SESSION_COORDINATOR) {
      sessionCoordinator.stopStream('host:operator_stop');
      return;
    }
    context.targetFunctions.stopStream();
  };

  const [canUpdateTitle, setCanUpdateTitle] = useState(true);
  useEffect(() => {
    // if we are starting or stopping, we should not be able to update the title
    const isStarting = context.stateStores.hostState.status === 'starting';
    const isStopping = context.stateStores.hostState.status === 'stopping';
    setCanUpdateTitle(!isStarting && !isStopping);
  }, [context.stateStores.hostState.status]);

  // const handlePauseInput = () => {
  //   context.targetFunctions.pauseInput(!inputPaused);
  // };

  const onStreamTitleBlur = () => {
    setStreamTitle(context.stateStores.invitationStateV2.description ?? '');
    setShowSave(false);
    setSavedTitle(context.stateStores.invitationStateV2.description ?? '');
    context.targetFunctions.updateInvitationDescription(streamTitle);
  };

  return (
    <div className='flex flex-1 flex-col items-center overflow-y-auto p-4'>
      <div className={clsx(isElectron ? 'max-w-[580px]' : 'max-w-[1280px]', 'flex h-fit w-full flex-col gap-4')}>
        <section className='flex flex-col gap-4'>
          <div className='flex flex-col justify-between gap-4 md:flex-row md:items-start'>
            <div className='order-last w-full md:order-first md:min-w-0 md:flex-1'>
              <DisplayPicker choiceState={[selectedChoice, setSelectedChoice]} />
            </div>
            <div className='relative w-full md:min-w-0 md:flex-1'>
              <Input
                ref={streamTitleInputRef}
                type='text'
                disabled={!canUpdateTitle}
                onChange={handleStreamTitleChanged}
                value={streamTitle}
                className='theme-midnight:shadow-[0px_0px_4px_1px_rgba(99,210,218,0.6)] theme-midnight:border-dark theme-gray:border-brighter theme-gray:placeholder:text-white/40 w-full border-2 bg-gray-800 p-2 pr-10 drop-shadow-lg placeholder:text-white focus:outline-none focus:ring-0'
                placeholder='Playcast Title...'
                data-testid='stream-title-input'
              />
              {showSave && (
                <button
                  onClick={onStreamTitleBlur}
                  className='absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-white'
                  data-testid='save-title-button'
                >
                  <FontAwesomeIcon icon={faFloppyDisk} className='text-emerald-400' />
                </button>
              )}
            </div>
          </div>
          <Display height='lg' selectedChoice={selectedChoice} />
        </section>

        <section className='flex flex-1 flex-col gap-4 md:flex-row'>
          <div className='flex w-full md:w-1/2' tabIndex={-1}>
            <VolumeManager />
          </div>
          <div className='relative mt-auto flex w-full flex-col gap-y-4 md:w-1/2'>
            <div className='flex gap-2'>
              <PlayerInputOptions />
              <InviteButton
                text='Share'
                icon={faUserPlus}
                position='top'
                tooltip={
                  isLive
                    ? !(
                        context.stateStores.invitationStateV2.type === 'lobby' &&
                        context.stateStores.invitationStateV2.manifest.lobbyOptions.canJoin
                      )
                      ? 'Friends cannot join when lobby is closed'
                      : undefined
                    : 'Start a session to invite friends!'
                }
                className='bg-primary hover:bg-primary/60 focus:bg-primary active:bg-primary theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] m-0 size-full border-none px-2 text-white'
              />
            </div>
            <div className='bottom-0 hidden flex-1 md:flex'>
              <LiveActionButton
                isLive={isLive}
                buttonText={buttonText}
                handleStopStream={handleStopStream}
                handleStartLoginStream={handleStartLoginStream}
              />
            </div>
          </div>
        </section>

        <div className='sticky bottom-0 flex flex-1 md:hidden'>
          <LiveActionButton
            isLive={isLive}
            buttonText={buttonText}
            handleStopStream={handleStopStream}
            handleStartLoginStream={handleStartLoginStream}
          />
        </div>
      </div>
    </div>
  );
};
